/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid,ThemeProvider, Box, IconButton, TextField, Tooltip, Typography,InputAdornment} from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { theme } from '../../../../../components/Medical_History_Forms/themeprovider';
import EditableTable from '../../../../../components/customTableComponent';
import { ColumnConfig, GroupConfig, TableRowData } from '../../../../../types';
import { deleteInvestigation, getInvestigation } from '../../../../../services/apiService';
import { useLoader } from '../../../../../components/loaderProvider/loader';
import addCircle from '../../../../../assets/SVG/add-black.svg';
import { usePatientContext } from '../../../../../components/SubPatinetProvider';
import deleteBtn from '../../../../../assets/SVG/trash.svg';
import { useAlert } from '../../../../../components/alertProvider';
import { ConfirmationModal } from '../../../../../components/confirmationModal';
import RemarkPopover from '../../../../../components/Billing/common/RemarkPopover';
import { ReactComponent as SearchIcon } from '../../../../../assets/SVG/Search-Icon.svg';
import { formatDate } from '../../../../../utils/dateUtils';


type OnDataType = (value: boolean) => void;
interface CategoryDetailsProps {
  onData: OnDataType;
  permissions?: { canAdd?: boolean; canEdit?: boolean };
  onDataCountChange?: (count: number) => void;
}

const CategoryDetails = ({ onData, permissions, onDataCountChange }: CategoryDetailsProps) => {
  const { t } = useTranslation('general');

  const columnsMale: ColumnConfig[] = [
    { field: 'created_on', headerName: t('Date'), type: 'text', editable: false },
    { field: 'identifier', headerName: t('TestCode'), type: 'text', editable: false },
    { field: 'name', headerName: t('TestName'), type: 'text', editable: false },
    { field: 'doctor_name', headerName: t('DoctorName'), type: 'text', editable: false },
    { field: 'clinic', headerName: t('Clinic'), type: 'text', editable: false },
    { field: 'remark', headerName: t('Remark'), type: 'text', editable: false },
    { field: 'action', headerName: '', type: 'text', editable: false },
  ];
  const groupConfigMale: GroupConfig[] = [
    { fields: ['created_on'], type: 'column' },
    { fields: ['identifier'], type: 'column' },
    { fields: ['name'], type: 'column' },
    { fields: ['doctor_name'], type: 'column' },
    { fields: ['clinic'], type: 'column' },
    { fields: ['remark'], type: 'column' },
    { fields: ['action'], type: 'column' },
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const { patientId } = usePatientContext();
  const { showLoader, hideLoader } = useLoader();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false); 
  const onDataRef = useRef<OnDataType | null>(null); // Initialize with null
  const [selectedDigId, setselecteddigId] = useState<number | null>(null);
  const [deleteRefreshCounter, setDeleteRefreshCounter] = useState(0);
  const { addAlert } = useAlert();

  // Remark popover state
  const [remarkAnchorEl, setRemarkAnchorEl] = useState<null | HTMLElement>(null);
  const [remarkText, setRemarkText] = useState<string>('');

  const handleOpenRemark = (event: React.MouseEvent<HTMLElement>, text: string) => {
    setRemarkAnchorEl(event.currentTarget);
    setRemarkText(text);
  };

  const handleCloseRemark = () => {
    setRemarkAnchorEl(null);
    setRemarkText('');
  };

  const handleDelete = (id: number) => {
    setselecteddigId(id);
    setOpenConfirmDialog(true);
  };
  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);
  

  const onBack = () => {
    onData(true);
  };

  const [data, setData] = useState<TableRowData[]>([]);
  const [dataSize, setDataSize] = useState<number>(0);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const handleOnChange = (id: number, field: string, value: string) => {};

  // Debounced search
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // When the search term is cleared, reset immediately without debounce
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      setPage(0); // Reset to first page immediately when search is cleared
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page when search changes
    }, 500); // 500ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(()=>{
    showLoader();
    const offset = page * rowsPerPage;
    let query = `?patient_id=${patientId}&limit=${rowsPerPage}&offset=${offset}`;
    if (debouncedSearchTerm.trim()) {
      query += `&search=${encodeURIComponent(debouncedSearchTerm.trim())}`;
    }
    getInvestigation(query)
      .then((res) => {
        const mappedData = res.objects.map((obj) => {
          const createdOnDate = obj.created_on ? new Date(obj.created_on) : null;
          const isDisabled = createdOnDate
            ? (new Date().getTime() - createdOnDate.getTime()) / (1000 * 60 * 60) > 24
            : false;
  
          return {
            created_on: formatDate(obj.created_on) || '-',  // Format date to dd-mm-yyyy
            doctor_name: obj.doctor_first_name
              ? (() => {
                const firstName = obj.doctor_first_name ?? '';
                const lastName = obj.doctor_last_name ?? '';
                const fullName = `${firstName} ${lastName}`.trim();
                return firstName.startsWith('Dr.') ? fullName : `Dr. ${fullName}`;
              })()
              : '-',
            id: obj.id,
            identifier: obj.laboratory_test.identifier,
            name: obj.laboratory_test.name,
            clinic: obj.clinic || '-',
            remark: (() => {
              const remarkValue = obj.remark || '-';
              if (remarkValue && remarkValue.trim() !== '-' && remarkValue.length > 30) {
                const displayText = remarkValue.slice(0, 30) + '...';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Tooltip title={t('Remark')} arrow>
                      <IconButton
                        size="small"
                        sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                        aria-label={t('ViewRemark')}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          handleOpenRemark(e, remarkValue);
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography>{displayText}</Typography>
                  </Box>
                );
              }
              return remarkValue;
            })(),
            action: permissions?.canEdit ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                <Tooltip title={isDisabled ? t('CannotDeletePast24Hours') : t('Delete')}>
                  <span>
                    <IconButton
                      onClick={() => !isDisabled && handleDelete(obj.id)}
                      disabled={isDisabled}
                      style={{
                        color: isDisabled ? '#B0B0B0' : '#FF0000',
                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                      }}
                      size="small"
                    >
                      <img src={deleteBtn} alt="delete" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            ) : null,
          };
        });
  
        setDataSize(res.meta.total_count);
        setData(mappedData);
        if (onDataCountChange) {
          onDataCountChange(res.meta.total_count);
        }
        hideLoader();
      })  
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching investigations:', error);
        addAlert(t('ErrorFetchingInvestigations'), 'error');
      })
      .finally(() => {
        hideLoader();

      });
    // eslint-disable-next-line
    },[patientId, showLoader, hideLoader, deleteRefreshCounter , t, page, debouncedSearchTerm]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage - 1);
  };

  const handleSubmit =()=>{
    showLoader();
    if (selectedDigId) {
      deleteInvestigation(selectedDigId).then(()=>{
        addAlert(t('ServiceDeletedSuccessfully'), 'success');
        setDeleteRefreshCounter(prev => prev + 1);
      })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Error deleting selected service');
          addAlert(t('ErrorDeletingSelectedService'), 'error');

        })
        .finally(() => {
          hideLoader();
        });
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
  };


  return (
    <Box mt={3}>
      <ThemeProvider theme={theme}>
        <div>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6} lg={4} xl={4}>
              <TextField
                id="outlined-basic"
                placeholder={t('SearchByTestName')}
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>
            <Grid item xs />
            {permissions?.canAdd && (
              <Grid item xs={12} sm="auto">
                <button onClick={onBack} style={  {padding: '3px 20px', border: 'none', borderRadius: '7px', fontSize:'18px', fontWeight:'700', backgroundColor:'#F3F3F3F3', color:'#505050' }}>
                  <img style={{padding:'5px', width:'37px', height:'37px'}} src={addCircle} alt='add'/>   {t('Add')}
                </button>
              </Grid>
            )}
          </Grid>
          {/* <Grid container spacing={2} marginTop={2} >
            <Grid item xs={5} display="flex" justifyContent="start">
              <IconButton onClick={onBack}>
                <img src={HPIBack} alt="Back" style={{ width: '2.5rem', height: '2.5rem' }} />
              </IconButton>

            </Grid>

          </Grid> */}

        </div>

        <Box className='mt-4'>
          <Box className='mt-3'>
            <EditableTable
              columns={columnsMale}
              data={data}
              actionType="none"
              onChange={handleOnChange}
              groupConfig={groupConfigMale}
              dataSize={dataSize}
              currentPageoffset={page + 1}
              onPageChange={handleChangePage}
            />
            {/* {data.length === 0 && (
              <Box textAlign="center" padding={2}>
                <p>No data found</p>
              </Box>
            )} */}
          </Box>
        </Box>
        <ConfirmationModal
          open={openConfirmDialog}
          handleClose={()=>{setOpenConfirmDialog(false);}}
          onConfirm={() => {
            handleSubmit();
          }}
          type="delete"
          title={t('DeleteService')}
          contentMessage={t('AreYouSureDeletePrescribedService')}
        />
        <RemarkPopover
          anchorEl={remarkAnchorEl}
          content={remarkText}
          open={Boolean(remarkAnchorEl)}
          onClose={handleCloseRemark}
          title={t('Remark')}
        />
      </ThemeProvider>
    </Box>
  );
};

export default CategoryDetails;